'use client'

import React from 'react'
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import ConfigCheck from '../../../../components/ConfigCheck'
import BiometricCheck from '../../../../components/BiometricCheck'
import ProctoringGuidelines from '../../../../components/ProctoringGuidelines'
import Assessment from './assessment'
import moment from 'moment'
import Timer from 'components/Timer'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import Bulb from '../../../../components/icons/Bulb'
import { Modal } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import {
  resetResponses,
  fetchAssessmentQuestions,
} from 'modules/Assessments/actions' // ADDED: Import fetchAssessmentQuestions
import selectors from '../../selectors'

import {
  PageContainer,
  ContentWrapper,
  StartButton,
  InstructionCard,
  InstructionHeader,
  InstructionTitle,
  InstructionDuration,
  InstructionDeadline,
  ImportantNotice,
  NoticeText,
  InstructionSectionTitle,
  InstructionList,
  InstructionListItem,
  TakeAssessmentButton,
  ContactInfo,
} from '../../Style'

const AptitudeInstructions = ({
  assessment,
  questions,
  totalQuestions,
  error,
  responses,
  saveQuestionResponse,
  studentId,
  setAssessment,
}) => {
  const location = useLocation()
  const dispatch = useDispatch()

  // Group all useState hooks together at the top
  const [loading, setLoading] = useState(true)
  const [showConfig, setShowConfig] = useState(false)
  const [allChecksComplete, setAllChecksComplete] = useState(false)
  const [showAssessment, setShowAssessment] = useState(false)
  const [isTimerActive, setIsTimerActive] = useState(false)
  const [countdown, setCountdown] = useState(null)
  const [timerPhase, setTimerPhase] = useState('initial')
  const [questionsLoaded, setQuestionsLoaded] = useState(false)
  const [configPassed, setConfigPassed] = useState(false)
  // Guards against rapid re-clicks on "Start Assessment" while the question
  // fetch/generation (1-2s) is in flight, preventing duplicate requests.
  const [isStarting, setIsStarting] = useState(false)

  const verificationEnabled = assessment?.allowVerification !== false

  // Get questions from Redux state
  const questionsFromRedux = useSelector(selectors.getQuestions)
  const questionsLoading = useSelector(selectors.getLoading)
  const questionsError = useSelector(selectors.getError)

  // Group useEffects together
  useEffect(() => {
    setTimeout(() => {
      setLoading(false)
    }, 1500)
  }, [])

  // ...existing code...
  useEffect(() => {
    console.log('🔍 Questions data changed:', questionsFromRedux)

    const sections = [
      'critical reasoning',
      'quantitative',
      'logical reasoning',
    ]

    // Check for questionsBySection directly on questionsFromRedux
    if (
      questionsFromRedux &&
      questionsFromRedux.questionsBySection
    ) {
      const hasQuestions = sections.some(
        section =>
          questionsFromRedux.questionsBySection[section] &&
          Array.isArray(questionsFromRedux.questionsBySection[section]) &&
          questionsFromRedux.questionsBySection[section].length > 0
      )

      if (hasQuestions) {
        console.log(
          '✅ Questions loaded successfully:',
          questionsFromRedux.questionsBySection
        )
        setQuestionsLoaded(true)
      }
    } else if (questionsFromRedux && typeof questionsFromRedux === 'object') {
      // Handle case where questions are directly in the object
      const hasQuestions = sections.some(
        section =>
          questionsFromRedux[section] &&
          Array.isArray(questionsFromRedux[section]) &&
          questionsFromRedux[section].length > 0
      )

      if (hasQuestions) {
        console.log(
          '✅ Questions loaded successfully (direct):',
          questionsFromRedux
        )
        setQuestionsLoaded(true)
      }
    }
  }, [questionsFromRedux])

  // Early return for loading state
  if (loading) {
    return <LoadingSpinner />
  }

  const handleTakeAssessment = () => {
    setShowConfig(true)
  }

  const handleCancelConfig = () => {
    setShowConfig(false)
  }

  const handleStart = async () => {
    if (!allChecksComplete) return
    // Re-entry guard: ignore repeat clicks while a start is already in progress.
    // Covers every caller (the Start button below and BiometricCheck's
    // onStartAssessment) so impatient double-clicks can't fire duplicate
    // getAssessmentQuestions requests during the 1-2s set generation.
    if (isStarting) return
    setIsStarting(true)

    try {
    if (assessment?.assessment_assigned_id) {
      // Clear any existing responses before fetching new questions
      dispatch(resetResponses())

      // Set current assessment in Redux first
      dispatch({
        type: 'SET_CURRENT_ASSESSMENT_ID',
        payload: {
          assessment_id: assessment.assessment_id || assessment.id,
          assessment_set_id: assessment.assessment_set_id,
          assessment_assigned_id: assessment.assessment_assigned_id,
          allowProctoring: assessment.allowProctoring || false,
          title: assessment.title,
          type: assessment.type,
          isPractice: assessment.isPractice || false,
          isCorporate: assessment.isCorporate,
          AptitudeDiagnosisRemain: assessment.AptitudeDiagnosisRemain,
          CommunicationDiagnosisRemain: assessment.CommunicationDiagnosisRemain,
        },
      })

      // Fetch questions and only proceed if successful
      const result = await dispatch(fetchAssessmentQuestions(assessment.assessment_assigned_id))
      if (!result || !result.success) {
        console.error('Failed to fetch assessment questions')
        return
      }
    } else {
      console.error('No assessment_assigned_id found!')
      return
    }

    setIsTimerActive(true)
    setShowConfig(false) // Close the modal
    setTimerPhase('initial')

    // Phase 1: Show "Timer starts now" for 2 seconds
    setTimeout(() => {
      setTimerPhase('countdown')
      setCountdown(3)

      // Phase 2: Start countdown
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval)
            setTimerPhase('start')

            // Phase 3: Show Start! and begin assessment
            setTimeout(() => {
              setShowAssessment(true)
            }, 1000)

            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, 2000)
    } catch (err) {
      console.error('Error starting assessment:', err)
    } finally {
      // Re-enable the Start button. On success the config modal is already
      // closing; on any failure/return this lets the user retry.
      setIsStarting(false)
    }
  }

  // Show assessment if questions are loaded and timer is complete
  if (showAssessment && questionsLoaded) {
    console.log('🎯 Showing assessment with questions:', questionsFromRedux)
    return (
      <Assessment
        questions={questionsFromRedux}
        totalQuestions={totalQuestions}
        loading={questionsLoading}
        error={questionsError || questionsError}
        responses={responses}
        saveQuestionResponse={saveQuestionResponse}
        startTime={new Date()}
        setAssessment={setAssessment}
      />
    )
  }

  // Show loading if questions are being fetched or timer is running but questions not loaded
  if (showAssessment && (!questionsLoaded || questionsLoading)) {
    console.log('🔄 Waiting for questions to load:', {
      showAssessment,
      questionsLoaded,
      questionsLoading,
      questionsFromRedux,
    })

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          flexDirection: 'column',
          background: '#f8fafc',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1000
        }}
      >
        <LoadingSpinner size={48} />
        <h2 style={{
          marginTop: '24px',
          fontSize: '20px',
          fontWeight: '600',
          color: '#1e293b',
          fontFamily: 'inherit'
        }}>
          Preparing your assessment...
        </h2>
        <p style={{
          marginTop: '8px',
          fontSize: '14px',
          color: '#64748b',
          fontFamily: 'inherit'
        }}>
          Please wait while we load your questions securely
        </p>
      </div>
    )
  }

  if (error || questionsError) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
        }}
      >
        <div style={{ color: 'red', fontSize: '16px' }}>
          Error: {error || questionsError}
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{ marginTop: '16px', padding: '8px 16px' }}
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <PageContainer>
      {/* <ContentWrapper> */}
      {isTimerActive ? (
        <Timer timerPhase={timerPhase} countdown={countdown} />
      ) : (
        <>
          {/* Add Back button at the top with same styling as Communication */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px',
              cursor: 'pointer',
            }}
            onClick={() => setAssessment(null)}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: '8px' }}
            >
              <path
                d="M19 12H5M5 12L12 19M5 12L12 5"
                stroke="#4F46E5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span
              style={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '16px',
                color: '#4F46E5',
              }}
            >
              Back to Assessments
            </span>
          </div>

          {/* Replace the conditional rendering with a Modal */}
          <Modal
            title="System Check"
            open={showConfig}
            onCancel={handleCancelConfig}
            footer={null}
            width={700}
            centered
            maskClosable={false}
            style={{ top: 20 }}
          >
            {!configPassed ? (
              <ConfigCheck
                onAllChecksComplete={() => {
                  setAllChecksComplete(true)
                  setConfigPassed(true)
                }}
              />
            ) : verificationEnabled ? (
              <BiometricCheck
                studentId={studentId}
                onStartAssessment={handleStart}
                isStarting={isStarting}
                assessmentType={assessment?.type || assessment?.assessment_type}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <style>{`@keyframes aptStartSpin { to { transform: rotate(360deg); } }`}</style>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
                  System Check Complete
                </h3>
                <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
                  Your device is ready. Click below to start the assessment.
                </p>
                <button
                  onClick={handleStart}
                  disabled={isStarting}
                  style={{
                    background: isStarting ? '#A5B4FC' : '#4F46E5',
                    color: '#ffffff',
                    border: 'none',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: isStarting ? 'not-allowed' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    minWidth: '200px',
                    transition: 'background 0.15s ease',
                  }}
                >
                  {isStarting && (
                    <span
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255,255,255,0.5)',
                        borderTopColor: '#ffffff',
                        borderRadius: '50%',
                        display: 'inline-block',
                        animation: 'aptStartSpin 0.7s linear infinite',
                      }}
                    />
                  )}
                  {isStarting ? 'Preparing…' : 'Start Assessment'}
                </button>
              </div>
            )}
          </Modal>

          <InstructionCard>
            <InstructionHeader>
              <div>
                <InstructionTitle>Aptitude Assessment</InstructionTitle>
                <div
                  style={{
                    fontSize: '30px',
                    fontWeight: '700',
                    color: '#000000',
                  }}
                >
                  {assessment?.title
                    ? assessment.title.charAt(0).toUpperCase() +
                    assessment.title.slice(1)
                    : 'Aptitude Test'}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <InstructionDeadline
                  style={{
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '22px',
                    lineHeight: '132%',
                    letterSpacing: '0%',
                  }}
                >
                  Ends on{'  '}
                  <span
                    style={{
                      color: '#3249D7',
                      marginLeft: '2px',
                      fontWeight: 'bold',
                    }}
                  >
                    {assessment?.endTime
                      ? moment.utc(assessment.endTime).format('Do MMMM, ha [IST]')
                      : '—'}
                  </span>
                </InstructionDeadline>
                <InstructionDuration>Duration - 45 mins</InstructionDuration>
              </div>
            </InstructionHeader>
          </InstructionCard>

          <InstructionCard>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <div style={{ marginRight: '8px' }}>
                <Bulb />
              </div>
              <span
                style={{
                  color: '#4F46E5',
                  fontWeight: '600',
                  fontSize: '26px',
                }}
              >
                Important!
              </span>
              <span
                style={{
                  marginLeft: '8px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '26px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                }}
              >
                Instructions
              </span>
            </div>

            <ImportantNotice>
              <NoticeText style={{ fontStyle: 'italic' }}>
                Assessment has to be taken in one go, please do not exit once
                started.
              </NoticeText>
            </ImportantNotice>

            <InstructionSectionTitle
              style={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '22px',
                lineHeight: '132%',
                letterSpacing: '0%',
              }}
            >
              Technical Setup
            </InstructionSectionTitle>
            <InstructionList>
              <InstructionListItem>
                Ensure a stable internet connection.
              </InstructionListItem>
              <InstructionListItem>
                Use a laptop/desktop with proper screen resolution.
              </InstructionListItem>
              <InstructionListItem>
                Close all unnecessary applications and browser tabs.
              </InstructionListItem>
            </InstructionList>

            <InstructionSectionTitle
              style={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '22px',
                lineHeight: '132%',
                letterSpacing: '0%',
              }}
            >
              Test Guidelines
            </InstructionSectionTitle>
            <InstructionList>
              <InstructionListItem>
                Read each question carefully before selecting your answer.
              </InstructionListItem>
              <InstructionListItem>
                You can navigate between questions using Next/Previous buttons.
              </InstructionListItem>
              <InstructionListItem>
                All questions are mandatory to answer.
              </InstructionListItem>
              <InstructionListItem>
                Time limit is strictly enforced - submit before time runs out.
              </InstructionListItem>
            </InstructionList>

            <InstructionSectionTitle
              style={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '22px',
                lineHeight: '132%',
                letterSpacing: '0%',
              }}
            >
              Assessment Rules
            </InstructionSectionTitle>
            <InstructionList>
              <InstructionListItem>
                Do not use any external resources or calculators.
              </InstructionListItem>
              <InstructionListItem>
                Stay focused and remain within the assessment interface.
              </InstructionListItem>
              <InstructionListItem>
                Do not refresh the page or navigate away from the test.
              </InstructionListItem>
            </InstructionList>

            <InstructionSectionTitle
              style={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '22px',
                lineHeight: '132%',
                letterSpacing: '0%',
              }}
            >
              Monitoring
            </InstructionSectionTitle>
            <InstructionList>
              <InstructionListItem>
                The system will monitor your screen and activities for
                proctoring purposes.
              </InstructionListItem>
              <InstructionListItem>
                Ensure you remain focused throughout the assessment.
              </InstructionListItem>
            </InstructionList>

            <ProctoringGuidelines proctoringEnabled={assessment?.allowProctoring !== false} />

            <ContactInfo
              style={{
                textAlign: 'left',
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '132%',
                letterSpacing: '0%',
                fontStyle: 'italic',
                marginBottom: '18px',
                marginLeft: '10px',
              }}
            >
              In case of technical issues, contact the assessment co-ordinator.
            </ContactInfo>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '24px',
              }}
            >
              <TakeAssessmentButton
                onClick={handleTakeAssessment}
                style={{
                  width: '302px',
                  height: '59px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  maxWidth: '100%',
                  padding: '0 24px',
                  marginTop: '24px',
                }}
              >
                Take assessment
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    marginLeft: '8px',
                    verticalAlign: 'middle',
                    position: 'relative',
                    top: '1px',
                  }}
                >
                  <path
                    d="M5 12H19M19 12L12 19M19 12L12 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </TakeAssessmentButton>
            </div>
          </InstructionCard>
        </>
      )}
      {/* </ContentWrapper> */}
    </PageContainer>
  )
}

export default AptitudeInstructions
